import {
  Box,
  Button,
  Field,
  Heading,
  Image,
  Input,
  NativeSelect,
  Text,
  VStack
} from "@chakra-ui/react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { useRegister } from "../../hooks/useAuthQueries"
import { getAuthErrorMessage } from "../../utils/authErrors"

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    company_name: "",
    company_number: "",
    organisation_type: "",
    first_name: "",
    last_name: "",
    email: "",
    password1: "",
    password2: ""
  })
  const { login: authLogin } = useAuth()
  const navigate = useNavigate()
  const registerMutation = useRegister()

  const getErrorMessage = (error: any) => getAuthErrorMessage(error)

  const getFieldError = (fieldName: string) => {
    return (registerMutation.error as any)?.response?.data?.[fieldName]?.[0]
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const response = await registerMutation.mutateAsync(formData)
      if (response.data.key) {
        authLogin(response.data.key)
      }
      navigate("/login")
    } catch (error: any) {
      console.error("Registration failed:", error)
    }
  }

  return (
    <>
      <Box py={8}>
        <Link to="/">
          <Image
            w={{ base: 150, md: 200 }}
            src="/greenflip_logo.png"
            alt="GreenFlip"
          />
        </Link>
      </Box>

      <Box
        bg="gray.100"
        px={{ base: 6, md: 8, lg: 12 }}
        py={{ base: 6, md: 8, lg: 10 }}
        borderRadius="3xl"
        w={{ base: "95%", md: "85%", lg: "80%" }}
        maxW={{ base: "500px", lg: "500px" }}
      >
        <Heading pb={8} size={{ base: "lg", md: "xl" }}>
          Create your account
        </Heading>
        <form onSubmit={handleSubmit}>
          <VStack gap={5} align="stretch">
            {registerMutation.error && (
              <Box
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
                color="red.800"
                px={4}
                py={3}
                borderRadius="md"
                fontSize="sm"
              >
                {getErrorMessage(registerMutation.error)}
              </Box>
            )}
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Company Name
              </Field.Label>
              <Input
                name="company_name"
                placeholder="Enter your company name"
                bg="white"
                autoFocus
                value={formData.company_name}
                onChange={(e) => handleInputChange("company_name", e.target.value)}
                required
                borderColor={getFieldError("company_name") ? "red.300" : undefined}
                _focus={{
                  borderColor: getFieldError("company_name") ? "red.500" : "green.500"
                }}
              />
              {getFieldError("company_name") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("company_name")}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Company Number
              </Field.Label>
              <Input
                name="company_number"
                placeholder="Enter your company registration number"
                bg="white"
                value={formData.company_number}
                onChange={(e) => handleInputChange("company_number", e.target.value)}
                required
                borderColor={getFieldError("company_number") ? "red.300" : undefined}
                _focus={{
                  borderColor: getFieldError("company_number") ? "red.500" : "green.500"
                }}
              />
              {getFieldError("company_number") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("company_number")}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Type of Organisation
              </Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  bg="white"
                  value={formData.organisation_type}
                  onChange={(e) => handleInputChange("organisation_type", e.target.value)}
                  required
                  borderColor={getFieldError("organisation_type") ? "red.300" : undefined}
                  _focus={{
                    borderColor: getFieldError("organisation_type") ? "red.500" : "green.500"
                  }}
                >
                  <option value="" disabled>Select organisation type</option>
                  <option value="property_investment">Property investment / Asset management</option>
                  <option value="housing_association">Housing association</option>
                  <option value="local_authority">Local authority</option>
                  <option value="other">Other</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              {getFieldError("organisation_type") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("organisation_type")}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                First Name
              </Field.Label>
              <Input
                name="first_name"
                placeholder="Enter your first name"
                bg="white"
                value={formData.first_name}
                onChange={(e) => handleInputChange("first_name", e.target.value)}
                required
                borderColor={getFieldError("first_name") ? "red.300" : undefined}
                _focus={{
                  borderColor: getFieldError("first_name") ? "red.500" : "green.500"
                }}
              />
              {getFieldError("first_name") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("first_name")}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Last Name
              </Field.Label>
              <Input
                name="last_name"
                placeholder="Enter your last name"
                bg="white"
                value={formData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                required
                borderColor={getFieldError("last_name") ? "red.300" : undefined}
                _focus={{
                  borderColor: getFieldError("last_name") ? "red.500" : "green.500"
                }}
              />
              {getFieldError("last_name") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("last_name")}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Email
              </Field.Label>
              <Input
                name="email"
                type="email"
                placeholder="Enter your email address"
                bg="white"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
                borderColor={getFieldError("email") ? "red.300" : undefined}
                _focus={{
                  borderColor: getFieldError("email") ? "red.500" : "green.500"
                }}
              />
              {getFieldError("email") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("email")}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Password
              </Field.Label>
              <Input
                name="password1"
                type="password"
                placeholder="Create a strong password"
                bg="white"
                value={formData.password1}
                onChange={(e) => handleInputChange("password1", e.target.value)}
                required
                borderColor={getFieldError("password1") ? "red.300" : undefined}
                _focus={{
                  borderColor: getFieldError("password1") ? "red.500" : "green.500"
                }}
              />
              {getFieldError("password1") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("password1")}
                </Text>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Confirm Password
              </Field.Label>
              <Input
                name="password2"
                type="password"
                placeholder="Confirm your password"
                bg="white"
                value={formData.password2}
                onChange={(e) => handleInputChange("password2", e.target.value)}
                required
                borderColor={getFieldError("password2") ? "red.300" : undefined}
                _focus={{
                  borderColor: getFieldError("password2") ? "red.500" : "green.500"
                }}
              />
              {getFieldError("password2") && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {getFieldError("password2")}
                </Text>
              )}
            </Field.Root>

            <Button
              type="submit"
              bg="green.800"
              color="white"
              _hover={{ bg: "green.900" }}
              size="lg"
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? "Creating account..." : "Create account"}
            </Button>
          </VStack>
        </form>
      </Box>
      <Text textAlign="center" pb={8}>
        Already have an account?{" "}
        <Text display="inline" color="green.800" fontWeight="bold">
          <Link to="/login">Sign in</Link>
        </Text>
      </Text>
    </>
  )
}

export default Signup
